import React, { useEffect, useMemo, useRef, useState } from 'react';
import AnimatedTitle from '../AnimatedTitle';
import './wordmonster.css';
import AudioPlayer from '../AudioPlayer';
import wordMonsterTheme from '../../sound/wordmonster/theme.mp3';

export default function WordMonsterHost({
  socket,
  sessionId,
  players,
  ipAddress,
  serverType,
  onReturnToPartyPack,
  removePlayer,
  setForceRemove,
  isMusicMuted = false,
}) {
  const [state, setState] = useState({
    phase: 'lobby',
    round: 1,
    prompt: null,
    targetWord: null,
    cravingMonster: null,
    roundRuleText: '',
    roundEndsAt: null,
    roundResult: null,
    recentEvents: [],
    scoreboard: [],
  });
  const [animatingWords, setAnimatingWords] = useState([]);
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const animationTimeoutsRef = useRef([]);
  const scoreboardRef = useRef([]);

  const sortedScores = useMemo(
    () => [...(state.scoreboard || [])].sort((a, b) => b.points - a.points),
    [state.scoreboard],
  );

  useEffect(() => {
    scoreboardRef.current = state.scoreboard || [];
  }, [state.scoreboard]);

  useEffect(() => {
    if (!socket) return undefined;

    const update = (incoming) => setState((prev) => ({ ...prev, ...incoming }));
    const onWordEvent = (event) => {
      const scoreRows = scoreboardRef.current;
      const total = Math.max(scoreRows.length, 1);
      const playerIndex = Math.max(scoreRows.findIndex((entry) => entry.id === event.playerId), 0);
      const fromPercent = ((playerIndex + 0.5) / total) * 100;

      const token = {
        id: `${event.id}-${Date.now()}`,
        word: event.word,
        fromPercent,
        direction: event.monsterType,
        speedMs: event.speedMs || 1200,
        stage: 'to-belt',
      };

      setAnimatingWords((prev) => [...prev, token]);

      const toMonsterTimeout = setTimeout(() => {
        setAnimatingWords((prev) => prev.map((row) => (
          row.id === token.id ? { ...row, stage: 'to-monster' } : row
        )));
      }, Math.max(260, Math.floor(token.speedMs * 0.55)));

      const cleanupTimeout = setTimeout(() => {
        setAnimatingWords((prev) => prev.filter((row) => row.id !== token.id));
      }, token.speedMs + 160);

      animationTimeoutsRef.current.push(toMonsterTimeout, cleanupTimeout);
    };

    socket.on('wordMonsterState', update);
    socket.on('wordMonsterWordEvent', onWordEvent);
    socket.emit('wordMonsterSyncState', { sessionId });

    return () => {
      socket.off('wordMonsterState', update);
      socket.off('wordMonsterWordEvent', onWordEvent);
      animationTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      animationTimeoutsRef.current = [];
    };
  }, [socket, sessionId]);

  useEffect(() => {
    if (state.phase !== 'active-round' || !state.roundEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(state.roundEndsAt - Date.now(), 0));
    };

    tick();
    const intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, [state.phase, state.roundEndsAt]);

  const cravingLabel = state.cravingMonster === 'synonyms' ? 'Synonym Monster' : 'Antonym Monster';
  const isProductionServer = serverType === 'fayaz.fly.dev';
  const showLiveFeed = Number(state.round || 1) === 1;
  const isThemePlaying = state.phase === 'lobby' || state.phase === 'active-round';

  return (
    <div className="wm-host-screen">
      <AudioPlayer audioSrc={wordMonsterTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="wm-top-bar">
        <div className="wm-join-message">
          <h2>
            {isProductionServer
              ? 'Production is selected in the server picker. Players should choose Production on their devices; no room code is needed.'
              : <>
                  Join at <span className="wm-accent">Fayaz.One</span> in your browser
                </>}
          </h2>
        </div>
        <div className="wm-room-info">
          {!isProductionServer && <h2>Room Code: {ipAddress}</h2>}
          <h4>Session: {sessionId}</h4>
        </div>
      </div>

      {state.phase === 'lobby' ? (
        <div className="wm-lobby-panel">
          <AnimatedTitle title="Word Monster" />
          <div className="wm-lobby-grid">
            <div className="wm-card">
              <h4>Players</h4>
              {players.map((player) => (
                <button
                  className="wm-ghost-button"
                  key={player.socketId}
                  onClick={() => {
                    setForceRemove(true);
                    removePlayer(player.socketId, true);
                  }}
                >
                  {player.name}
                </button>
              ))}
            </div>
            <div className="wm-card">
              <h2>Game Settings</h2>
              <p>Three rounds, 30 seconds each.</p>
              <p>Players can submit as many words as they can type.</p>
              <p>Need at least 2 players to begin.</p>
            </div>
          </div>
          <button className="wm-primary-button" onClick={() => socket.emit('wordMonsterStart', { sessionId })} disabled={players.length < 2}>
            {players.length < 2 ? 'Need at least 2 players' : 'Start Game'}
          </button>
        </div>
      ) : (
        <div className="wm-factory-shell">
          <div className="wm-score-row">
            {(state.scoreboard || []).map((entry) => (
              <div key={entry.id} className="wm-avatar-card">
                <div className="wm-avatar-icon">{entry.avatar || '😺'}</div>
                <div className="wm-avatar-name">{entry.name}</div>
                <div className="wm-avatar-points">{entry.points}</div>
              </div>
            ))}
          </div>

          <div className="wm-center-stage">
            <div className="wm-round-chip">Round {state.round}</div>
            <div className="wm-target-word">{state.prompt || 'WORD'}</div>

            <div className="wm-monster-zone">
              <div className={`wm-monster wm-monster-syn ${state.cravingMonster === 'synonyms' ? 'wm-craving' : ''}`}>
                <div className="wm-monster-label">Synonym Monster</div>
              </div>

              <div className="wm-sorter-column">
                <div className="wm-neutral-monster">Sorter</div>
                <div className="wm-conveyor" />
                <div className="wm-conveyor-rail" />
              </div>

              <div className={`wm-monster wm-monster-ant ${state.cravingMonster === 'antonyms' ? 'wm-craving' : ''}`}>
                <div className="wm-monster-label">Antonym Monster</div>
              </div>
            </div>

            {state.phase === 'active-round' && (
              <div className="wm-timer">Time Left: {(timeLeftMs / 1000).toFixed(1)}s</div>
            )}

            {state.phase === 'round-intro' && (
              <div className="wm-overlay-card">
                <h3>Before Round {state.round}</h3>
                <p>{state.roundRuleText}</p>
                {state.round === 3 && <p>Craving Monster: {cravingLabel}</p>}
                <button className="wm-primary-button" onClick={() => socket.emit('wordMonsterBeginRound', { sessionId })}>Begin 30s Round</button>
              </div>
            )}

            {state.phase === 'round-summary' && (
              <div className="wm-overlay-card">
                <h3>Round {state.round} Complete</h3>
                <p>Submissions: {state.roundResult?.submissions ?? 0}</p>
                <p>Valid: {state.roundResult?.valid ?? 0} / Duplicate: {state.roundResult?.duplicates ?? 0}</p>
                <button className="wm-primary-button" onClick={() => socket.emit('wordMonsterContinue', { sessionId })}>Next Round</button>
              </div>
            )}

            {state.phase === 'game-over' && (
              <div className="wm-overlay-card">
                <h3>Game Over</h3>
                {sortedScores.map((entry, index) => (
                  <div key={entry.id} className="wm-final-score">
                    <span>#{index + 1} {entry.avatar} {entry.name}</span>
                    <strong>{entry.points}</strong>
                  </div>
                ))}
                <button className="wm-primary-button" onClick={() => socket.emit('wordMonsterStart', { sessionId })}>Play Again</button>
                <button className="wm-ghost-button" onClick={onReturnToPartyPack}>Return to Party Pack</button>
              </div>
            )}

            <div className="wm-animation-layer">
              {animatingWords.map((token) => (
                <div
                  key={token.id}
                  className={`wm-flying-word ${token.stage} ${token.direction || 'invalid'}`}
                  style={{
                    '--from-x': `${token.fromPercent}%`,
                    '--speed-ms': `${token.speedMs}ms`,
                  }}
                >
                  {token.word}
                </div>
              ))}
            </div>

            {showLiveFeed && (
              <div className="wm-live-log">
                <h4>Live Feed</h4>
                {(state.recentEvents || []).slice(-10).reverse().map((event) => (
                  <div key={event.id} className="wm-log-item">
                    <span>{event.playerAvatar} {event.playerName}: {event.word}</span>
                    <strong>{event.delta > 0 ? `+${event.delta}` : event.delta}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
